import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug } from '../lib/cms';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadAboutData() {
      const data = await fetchPageBySlug('om-heidi');
      setPageData(data);
      setLoading(false);
    }
    loadAboutData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-sand-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center bg-sand-50 text-center px-6">
        <h2 className="font-serif text-2xl font-bold text-black">Siden blev ikke fundet</h2>
        <p className="text-foreground/70 mt-2">Vi kunne ikke hente biografien i øjeblikket.</p>
        <Link to="/" className="mt-6 rounded-full bg-clay-700 px-6 py-2 text-sm text-sand-50">Gå til forsiden</Link>
      </div>
    );
  }

  const contentHtml = pageData.content?.da || '';
  const heroImage = pageData.hero_image?.[0] || 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/billede-til-siden-bio.jpg';

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={`${heroImage}?width=1920&quality=60&format=auto`}
            alt="Heidi Svelmøe"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Om Heidi Svelmøe
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Lær sangerinden, sangskriveren og sangunderviseren bag Sang & Mindfulness at kende.
          </p>
        </div>
      </section>

      {/* Main Biography Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
            {/* Left: Portrait Image */}
            <div className="md:col-span-5 md:sticky md:top-36 xl:md:top-40">
              <div className="relative">
                <img
                  src={`${heroImage}?width=500&quality=85&format=auto`}
                  alt="Heidi Svelmøe Portræt"
                  className="relative z-10 w-full rounded-none shadow-lg object-cover aspect-[3/4]"
                />
              </div>
              <div className="mt-6 text-center md:text-left">
                <span className="text-xs uppercase tracking-wider text-sage-600 font-semibold block">
                  Undervisningssted
                </span>
                <p className="text-sm text-foreground/80 mt-1">
                  Solvognens Solsal, Kronprinsensgade 7b<br />
                  5000 Odense C (lige ved Eventyrhaven)
                </p>
              </div>
            </div>

            {/* Right: Rich Text Content */}
            <div className="md:col-span-7">
              <div
                className="prose prose-sand prose-headings:font-serif prose-headings:text-black prose-headings:font-bold prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-3 prose-p:text-foreground/80 prose-p:leading-relaxed prose-strong:text-clay-900 max-w-none text-left flex flex-col gap-4"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />

              {/* Call to Action */}
              <div className="mt-12 pt-8 border-t border-sand-200 flex flex-col sm:flex-row items-center gap-4">
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto text-center rounded-full bg-cta px-8 py-3 text-sm uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-md active:scale-98"
                >
                  Book en tid
                </Link>
                <Link
                  to="/undervisning"
                  className="w-full sm:w-auto text-center rounded-full border border-cta text-clay-800 hover:bg-cta hover:text-clay-900 px-8 py-3 text-sm uppercase tracking-widest font-semibold transition-all"
                >
                  Se undervisning
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
